import { Base64 } from 'js-base64'
import Cookies from 'js-cookie'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'

import { jwtKey, mehiAvCookie } from '../../constants'
import { preSelectedUserOmIdAtom } from '../../state/reserve/atoms'
import { loginStatusAtom, ohcAllowedStatusAtom } from '../../state/user/atoms'
import api from '../services/api'
import authenticationService from '../services/authenticationService'
import { getAV3HomeUrl } from '../utils/url'

export type LoginStatus = 'authenticated' | 'impersonated' | 'external' | undefined

if (typeof window !== 'undefined') {
  setInterval(async () => {
    if (Cookies.get(mehiAvCookie)) {
      try {
        await api.v1.heartbeat()
      } catch {
        // NO-OP
      }
    }
  }, 15000)
}

export const isJwtValid = (token?: string | null): token is string => {
  let tokenToValidate = token
  if (tokenToValidate?.startsWith('Partner')) {
    return true
  }
  if (tokenToValidate?.startsWith('External') || token?.startsWith('Test')) {
    tokenToValidate = tokenToValidate?.split(' ')[1]
  }
  try {
    if (!tokenToValidate) return false
    const jwtData: { exp: number } = JSON.parse(Base64.decode(tokenToValidate.split('.')[1]))
    return jwtData.exp > new Date().getTime() / 1000
  } catch (error) {
    return false
  }
}

interface LoginStateContextType {
  loginStatus: LoginStatus
  logout(): void
  setApiToken(token: string): void
}

const LoginStateContext = createContext<LoginStateContextType>({
  loginStatus: undefined,
  logout: () => {
    /* placeholder */
  },
  setApiToken: () => {
    /* placeholder */
  },
})

export const LoginStateProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const location = useLocation()
  const omIdParam = new URLSearchParams(location.search).get('omUid')
  const setPreSelectedUser = useSetRecoilState(preSelectedUserOmIdAtom)
  if (omIdParam) {
    setPreSelectedUser(parseInt(omIdParam))
  }
  const [loginStatus, setLoginStatus] = useRecoilState(loginStatusAtom)
  const setOhcAllowedStatus = useSetRecoilState(ohcAllowedStatusAtom)

  const logout = useCallback(async () => {
    if (Cookies.get(mehiAvCookie)) {
      await authenticationService.logout()
    }
    sessionStorage.removeItem(jwtKey)
    setLoginStatus(undefined)
    setOhcAllowedStatus(null)

    window.location.assign(getAV3HomeUrl())
  }, [setLoginStatus, setOhcAllowedStatus])

  const setApiToken = useCallback(async (token: string) => {
    if (Cookies.get(mehiAvCookie)) {
      await authenticationService.logout()
    }
    api.http.setJwt(token)
    sessionStorage.setItem(jwtKey, token)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      const hasJwt =
        Boolean(sessionStorage.getItem(jwtKey)) && isJwtValid(sessionStorage.getItem(jwtKey))
      const hasImpersonationJwt =
        hasJwt &&
        !sessionStorage.getItem(jwtKey)?.startsWith('Test') &&
        !sessionStorage.getItem(jwtKey)?.startsWith('External') &&
        !sessionStorage.getItem(jwtKey)?.startsWith('Partner')
      const hasTestJwt = hasJwt && sessionStorage.getItem(jwtKey)?.startsWith('Test')
      const hasExtJwt =
        hasJwt &&
        (sessionStorage.getItem(jwtKey)?.startsWith('External') ||
          sessionStorage.getItem(jwtKey)?.startsWith('Partner'))
      const hasCookieAuth = Cookies.get(mehiAvCookie)

      if (!loginStatus) {
        if (hasCookieAuth || hasTestJwt) {
          setLoginStatus('authenticated')
        } else if (hasImpersonationJwt) {
          setLoginStatus('impersonated')
        } else if (hasExtJwt) {
          setLoginStatus('external')
        }
      }

      // Ensure API instance is in sync with jwt
      if (hasJwt) {
        api.http.setJwt(sessionStorage.getItem(jwtKey)!)
      }

      // Marked as logged in, but session/cookie expired
      if (loginStatus && !hasJwt && !hasCookieAuth) {
        sessionStorage.removeItem(jwtKey)
        setLoginStatus(undefined)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [loginStatus, setLoginStatus])

  useEffect(() => {
    function handleEvent(message: MessageEvent) {
      if (message.data) {
        // Token is coming from Cypress
        if (message.origin === 'Cypress') {
          const token = message.data
          if (!isJwtValid(token)) {
            return
          }
          setApiToken(token)
          return
        }

        // Token is coming from Mehidoc app
        if (message.origin === 'mehidoc-app') {
          const token = message.data
          if (!isJwtValid(token)) {
            return
          }
          setApiToken(token)
          return
        }

        // Token is coming from Mehidoc desktop (Aspa etc.)
        const messageOriginURL = new URL(message.origin)
        if (
          (message.data.mehiToken || message.data.extToken) &&
          message.source === window.opener &&
          messageOriginURL.hostname.includes('mehidoc') &&
          messageOriginURL.hostname.endsWith('mehilainen.fi')
        ) {
          const token = message.data.mehiToken
            ? message.data.mehiToken
            : message.data.extToken
            ? `External ${message.data.extToken}`
            : undefined

          if (!isJwtValid(token)) {
            return
          }

          setApiToken(token)
          window.opener.postMessage('TOKEN_TRANSFERRED', '*')
          return
        }
      }
    }
    window.addEventListener('message', handleEvent)

    return () => window.removeEventListener('message', handleEvent)
  }, [setApiToken])

  const memoed = useMemo(
    () => ({
      loginStatus,
      logout,
      setApiToken,
    }),
    [loginStatus, logout, setApiToken]
  )

  return <LoginStateContext.Provider value={memoed}>{children}</LoginStateContext.Provider>
}

const useLoginState = (onSessionExpire?: () => void): LoginStateContextType => {
  const { loginStatus, logout, setApiToken } = useContext(LoginStateContext)
  const prevLoginStatusRef = useRef<LoginStatus>(loginStatus)

  useEffect(() => {
    if (loginStatus === undefined && prevLoginStatusRef.current !== undefined) {
      onSessionExpire?.()
    }
    prevLoginStatusRef.current = loginStatus
  }, [loginStatus, onSessionExpire])

  return { loginStatus, logout, setApiToken }
}

export default useLoginState
